import type { PlanChangeRef } from "@/shared/repositories/PlanChangeRepository";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Undo/redo is scoped to this browser tab: only batches created here are
 * offered, so stale changes (or another user's) can't be reverted by accident.
 * sessionStorage survives reloads/HMR and dies with the tab.
 *
 * Storage is per line, because undo is per line. A save that moves several
 * lines has to file each batch under its own line or it is unreachable there.
 */

const DEPTH = 10;

type Stored = { changes: number[]; undone: number[] };

const EMPTY: Stored = { changes: [], undone: [] };

const keyOf = (assetId: number | null) =>
	`calendar-plan-session-${assetId ?? "none"}`;

const read = (assetId: number | null): Stored => {
	try {
		const parsed = JSON.parse(sessionStorage.getItem(keyOf(assetId)) ?? "null");
		return { changes: parsed?.changes ?? [], undone: parsed?.undone ?? [] };
	} catch {
		return EMPTY;
	}
};

const write = (assetId: number | null, value: Stored) =>
	sessionStorage.setItem(keyOf(assetId), JSON.stringify(value));

const push = (list: number[], id: number) =>
	list.includes(id) ? list : [...list.slice(-(DEPTH - 1)), id];

export const useSessionPlanChanges = (assetId: number | null) => {
	const [state, setState] = useState<Stored>(() => read(assetId));
	// Mirrors state so several refs recorded in one call accumulate instead of
	// each starting from the same stale snapshot
	const stateRef = useRef(state);
	const loadedFor = useRef(assetId);

	// Only reload when the line actually changes. Every mutator below writes
	// through, so there is no save-effect that could race this one and file the
	// previous line's ids under the new key.
	useEffect(() => {
		if (loadedFor.current === assetId) return;
		loadedFor.current = assetId;
		const loaded = read(assetId);
		stateRef.current = loaded;
		setState(loaded);
	}, [assetId]);

	const commit = useCallback(
		(next: Stored) => {
			stateRef.current = next;
			write(assetId, next);
			setState(next);
		},
		[assetId],
	);

	const recordChanges = useCallback(
		(refs: PlanChangeRef[]) => {
			for (const ref of refs) {
				if (ref.assetId === assetId) {
					const cur = stateRef.current;
					commit({ ...cur, changes: push(cur.changes, ref.planChangeId) });
				} else {
					// Another line's batch: write straight to its key, no state here
					const other = read(ref.assetId);
					write(ref.assetId, {
						...other,
						changes: push(other.changes, ref.planChangeId),
					});
				}
			}
		},
		[assetId, commit],
	);

	const markUndone = useCallback(
		(id: number) => {
			const cur = stateRef.current;
			commit({
				changes: cur.changes.filter((i) => i !== id),
				undone: push(cur.undone, id),
			});
		},
		[commit],
	);

	const markRedone = useCallback(
		(id: number) => {
			const cur = stateRef.current;
			commit({
				changes: push(cur.changes, id),
				undone: cur.undone.filter((i) => i !== id),
			});
		},
		[commit],
	);

	return {
		changes: state.changes,
		undone: state.undone,
		recordChanges,
		markUndone,
		markRedone,
	};
};
